import { QueryClient, QueryKey } from '@tanstack/react-query';
import { ServerActionResult } from '@/shared/types/server-action.type';
import { withAuth } from './withAuth';

interface SafeFetchQueryOptions<TData> {
	queryKey: QueryKey;
	action: () => Promise<ServerActionResult<TData>>;
	staleTime?: number;
}

/**
 * Imperatively fetches data using a server action with automatic auth retry.
 * Use this for one-off reads inside callbacks instead of useSafeMutation.
 *
 * @example
 * const queryClient = useQueryClient();
 * const urlData = await safeFetchQuery(queryClient, {
 *   queryKey: ['asset-url', assetId],
 *   action: () => getAssetUrl(assetId),
 * });
 */
export async function safeFetchQuery<TData>(
	queryClient: QueryClient,
	options: SafeFetchQueryOptions<TData>
): Promise<TData> {
	const { queryKey, action, staleTime } = options;

	const result = await queryClient.fetchQuery({
		queryKey,
		queryFn: () => withAuth(action)(),
		staleTime,
	});

	if (!result.success) {
		throw new Error(
			result.error?.message ?? 'Something went wrong! Please try again.'
		);
	}

	return result.data as TData;
}
